/**
 * The per-event frequency selector.
 *
 * A row is in one of two states and the control has to say which at a glance:
 *
 *  - **Default** — the row has no cadence of its own and tracks the tenant
 *    default. Rendered muted, prefixed "Default ·", so a screenful of rows
 *    reads as "these all follow the setting at the top".
 *  - **Custom** — the row was explicitly pinned. Rendered in full contrast
 *    with a Custom badge, and the per-row Reset puts it back to Default.
 *
 * A tooltip on the cell carries one sentence saying what the row will actually
 * do, so rows stay one line tall — the cadence alone cannot say it, because the same cadence means "its own
 * email" or "a count in the shared summary" depending on which mail box is
 * ticked. `Off` is the one value that delivers no email at all, and says so
 * rather than leaving the reader to infer it. `Hourly` used to be the second
 * such value — selectable but collected by nothing — and now describes a real
 * delivery like the other batched cadences.
 *
 * The tooltip sits on a wrapper because a disabled control gets no hover, and a
 * read-only admin still needs the sentence; it is repeated for screen readers.
 * Needs a `TooltipProvider` above it.
 */
import { Badge } from '../../../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '../../../ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../ui/tooltip';
import { BellOff, Mail } from 'lucide-react';
import {
  FREQUENCY_LABELS,
  INHERIT_FREQUENCY,
  type OneDailyDelivery,
  type SetupChoice,
} from './types';
import type { NotificationFrequency } from '../../../../lib/api';

/** What the inherit option says while the one daily digest governs the row. */
const ONE_DAILY_LABELS: Record<'digest' | 'bell', string> = {
  digest: 'Daily digest',
  bell: 'Bell only',
};

interface NotificationFrequencySelectProps {
  /** The row's own cadence, or `null` when it inherits. */
  value: NotificationFrequency | null;
  /**
   * What this row runs on while it inherits — the tenant default, or this
   * event's platform default when the tenant has set no blanket cadence.
   * Either way it is what the row will actually do, which is what the inherit
   * option has to say.
   */
  inheritedFrequency: NotificationFrequency;
  choices: SetupChoice[];
  disabled: boolean;
  /** Accessible name, e.g. `GRN dispute raised frequency`. */
  label: string;
  /** {@link describeDelivery} for this row's ticks and cadence. */
  deliveryHint: string;
  /** True when the pairing produces no email — shown with a struck-bell icon. */
  deliverySilent: boolean;
  /**
   * What the one daily digest does to this row. Either answer means the cadence
   * it inherits is not what it does, so the inherit option says so instead.
   */
  oneDaily?: OneDailyDelivery;
  onChange: (frequency: NotificationFrequency | null) => void;
}

export function NotificationFrequencySelect({
  value,
  inheritedFrequency,
  choices,
  disabled,
  label,
  deliveryHint,
  deliverySilent,
  oneDaily = null,
  onChange,
}: NotificationFrequencySelectProps) {
  const inherited = value === null;
  const defaultLabel = oneDaily
    ? ONE_DAILY_LABELS[oneDaily]
    : (FREQUENCY_LABELS[inheritedFrequency] ?? inheritedFrequency);
  const HintIcon = deliverySilent ? BellOff : Mail;

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5">
            <Select
              value={inherited ? INHERIT_FREQUENCY : value}
              disabled={disabled}
              onValueChange={(next) =>
                onChange(next === INHERIT_FREQUENCY ? null : (next as NotificationFrequency))
              }
            >
              <SelectTrigger
                aria-label={label}
                className={`h-8 w-[200px] ${
                  inherited ? 'border-dashed text-muted-foreground' : 'font-medium'
                } ${disabled ? 'pointer-events-none' : ''}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={INHERIT_FREQUENCY}>
                  Default · {defaultLabel}
                </SelectItem>
                <SelectSeparator />
                {choices.map((choice) => (
                  <SelectItem key={choice.value} value={choice.value}>
                    {choice.value === 'off' ? `${choice.label} — bell only` : choice.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <HintIcon aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            {!inherited && (
              <Badge variant="secondary" className="px-1.5 py-0 text-[10px] uppercase">
                Custom
              </Badge>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{deliveryHint}</TooltipContent>
      </Tooltip>
      <span className="sr-only">{deliveryHint}</span>
    </>
  );
}
